if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.zoomLevel = 1;
        this.maxZoomLevel = 4;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.activeDragPointerId = null;
        this.dragDistance = 0;
        this.suppressImageClick = false;
        this.mediaItems = [];

        const controls = this.querySelector('.product-media-modal__controls');
        if (controls) {
          // Dawn closes media modals when a mouse pointer is released outside
          // deferred media. Keep control interactions from reaching that handler.
          controls.addEventListener('pointerup', (event) => event.stopPropagation());
          controls.addEventListener('click', (event) => event.stopPropagation());

          controls.addEventListener('click', (event) => {
            const navButton = event.target.closest('.product-media-modal__nav');
            if (navButton) {
              this.showRelativeMedia(navButton.classList.contains('product-media-modal__nav--next') ? 1 : -1);
              return;
            }

            const zoomButton = event.target.closest('[data-zoom-action]');
            if (zoomButton) this.changeZoom(zoomButton.dataset.zoomAction);
          });
        }

        this.addEventListener('wheel', (event) => {
          if (!this.hasAttribute('open') || !this.getActiveImage()) return;
          event.preventDefault();
          this.setZoom(this.zoomLevel + (event.deltaY < 0 ? 0.25 : -0.25));
        }, { passive: false });

        // Clicking the active image changes zoom instead of closing the modal.
        // The clicked point is moved to the center of the browser window.
        this.addEventListener('click', (event) => this.handleImageClick(event), true);

        this.addEventListener('dblclick', (event) => {
          if (event.target !== this.getActiveImage()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);

        // Allow a zoomed image to be dragged with a grab-hand cursor.
        this.addEventListener('pointerdown', (event) => this.startImageDrag(event));
        this.addEventListener('pointermove', (event) => this.moveImageDrag(event));

        // Dawn installs its modal-closing pointerup listener before this class runs.
        // Handle drag release during the capture phase so the close listener never
        // receives pointerup from a zoomed image.
        this.addEventListener(
          'pointerup',
          (event) => this.finishImageDragBeforeModalClose(event),
          true
        );
        this.addEventListener(
          'pointercancel',
          (event) => this.finishImageDragBeforeModalClose(event),
          true
        );

        this.handleGalleryKeys = this.handleGalleryKeys.bind(this);
      }

      hide() {
        document.removeEventListener('keydown', this.handleGalleryKeys);
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.mediaItems = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
        this.showActiveMedia(opener.getAttribute('data-media-id'));
        document.addEventListener('keydown', this.handleGalleryKeys);
      }

      handleGalleryKeys(event) {
        if (event.key === 'ArrowRight') this.showRelativeMedia(1);
        if (event.key === 'ArrowLeft') this.showRelativeMedia(-1);
        if (event.key === '+' || event.key === '=') this.changeZoom('in');
        if (event.key === '-') this.changeZoom('out');
        if (event.key === '0') this.changeZoom('reset');
      }

      showActiveMedia(mediaId) {
        if (!mediaId) return;

        this.mediaItems.forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId);
        });

        const activeMedia = this.getActiveMedia();
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        this.resetZoom();
        activeMedia.scrollIntoView({ block: 'center', inline: 'center' });

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) activeMedia.loadContent();
      }

      showRelativeMedia(direction) {
        if (!this.mediaItems.length) return;
        const currentIndex = this.mediaItems.indexOf(this.getActiveMedia());
        const nextIndex = (currentIndex + direction + this.mediaItems.length) % this.mediaItems.length;
        this.showActiveMedia(this.mediaItems[nextIndex].getAttribute('data-media-id'));
      }

      getActiveMedia() {
        return this.querySelector('.product-media-modal__content > .active');
      }

      getActiveImage() {
        const activeMedia = this.getActiveMedia();
        return activeMedia && activeMedia.tagName === 'IMG' ? activeMedia : null;
      }

      changeZoom(action) {
        if (action === 'in') this.setZoom(this.zoomLevel + 0.25);
        if (action === 'out') this.setZoom(this.zoomLevel - 0.25);
        if (action === 'reset') this.setZoom(1);
      }

      startImageDrag(event) {
        const image = this.getActiveImage();
        if (event.target !== image || this.zoomLevel <= 1) return;

        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
        this.activeDragPointerId = event.pointerId;
        this.dragStartX = event.clientX - this.panX;
        this.dragStartY = event.clientY - this.panY;
        this.dragDistance = 0;
        this.suppressImageClick = false;
        image.classList.add('is-dragging');
        image.setPointerCapture(event.pointerId);
      }

      moveImageDrag(event) {
        if (!this.isDragging) return;
        const image = this.getActiveImage();
        if (!image) return;

        event.preventDefault();
        event.stopPropagation();
        const nextPanX = event.clientX - this.dragStartX;
        const nextPanY = event.clientY - this.dragStartY;
        this.dragDistance = Math.max(
          this.dragDistance,
          Math.hypot(nextPanX - this.panX, nextPanY - this.panY)
        );
        this.panX = nextPanX;
        this.panY = nextPanY;
        this.applyImageTransform(image);
      }

      finishImageDragBeforeModalClose(event) {
        const image = this.getActiveImage();
        if (event.target !== image && !this.isDragging) return;

        // Any release on the active image belongs to the image viewer, not to
        // Dawn's background-click-to-close behavior.
        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.isDragging && event.pointerId === this.activeDragPointerId) {
          this.suppressImageClick = this.dragDistance > 4;
          this.endImageDrag(event);
        }
      }

      handleImageClick(event) {
        const image = this.getActiveImage();
        if (event.target !== image) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.suppressImageClick) {
          this.suppressImageClick = false;
          return;
        }

        // Each click enlarges the image by 25% around the selected point
        // until the maximum zoom is reached. At maximum zoom, one click
        // restores the original 100% size and centered position.
        if (this.zoomLevel >= this.maxZoomLevel) {
          this.setZoom(1);
          return;
        }

        this.setZoomAroundPoint(
          Math.min(this.maxZoomLevel, this.zoomLevel + 0.25),
          event.clientX,
          event.clientY
        );
      }

      setZoomAroundPoint(level, clientX, clientY) {
        const image = this.getActiveImage();
        if (!image) return;

        const oldZoom = this.zoomLevel;
        const targetZoom = Math.min(this.maxZoomLevel, Math.max(0.5, level));
        const rect = image.getBoundingClientRect();
        const currentCenterX = rect.left + rect.width / 2;
        const currentCenterY = rect.top + rect.height / 2;
        const unpannedCenterX = currentCenterX - this.panX;
        const unpannedCenterY = currentCenterY - this.panY;
        const scaleRatio = targetZoom / oldZoom;
        const viewportCenterX = window.innerWidth / 2;
        const viewportCenterY = window.innerHeight / 2;
        const pointFromCurrentCenterX = clientX - currentCenterX;
        const pointFromCurrentCenterY = clientY - currentCenterY;

        this.panX = viewportCenterX - unpannedCenterX - pointFromCurrentCenterX * scaleRatio;
        this.panY = viewportCenterY - unpannedCenterY - pointFromCurrentCenterY * scaleRatio;
        this.setZoom(targetZoom, false);
      }

      endImageDrag(event) {
        if (!this.isDragging) return;
        const image = this.getActiveImage();
        this.isDragging = false;
        this.activeDragPointerId = null;

        if (image) {
          image.classList.remove('is-dragging');
          if (image.hasPointerCapture && image.hasPointerCapture(event.pointerId)) {
            image.releasePointerCapture(event.pointerId);
          }
        }
      }

      applyImageTransform(image = this.getActiveImage()) {
        if (!image) return;
        image.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`;
      }

      setZoom(level, resetPanAtNormalSize = true) {
        const image = this.getActiveImage();
        if (!image) return;
        this.zoomLevel = Math.min(this.maxZoomLevel, Math.max(0.5, level));

        if (this.zoomLevel <= 1 && resetPanAtNormalSize) {
          this.panX = 0;
          this.panY = 0;
          this.isDragging = false;
          this.activeDragPointerId = null;
          image.classList.remove('is-dragging');
        }

        this.applyImageTransform(image);
        image.classList.toggle('is-zoomed', this.zoomLevel > 1);
        image.classList.toggle('is-max-zoom', this.zoomLevel >= this.maxZoomLevel);
        image.classList.toggle('is-reduced', this.zoomLevel < 1);
        image.classList.toggle('is-original-size', this.zoomLevel === 1);
        const resetButton = this.querySelector('[data-zoom-action="reset"]');
        if (resetButton) resetButton.textContent = `${Math.round(this.zoomLevel * 100)}%`;
      }

      resetZoom() {
        this.querySelectorAll('.product-media-modal__content > img').forEach((image) => {
          image.style.transform = '';
          image.classList.remove('is-zoomed', 'is-max-zoom', 'is-reduced', 'is-original-size', 'is-dragging');
        });
        this.zoomLevel = 1;
        this.panX = 0;
        this.panY = 0;
        this.isDragging = false;
        this.activeDragPointerId = null;
        this.dragDistance = 0;
        this.suppressImageClick = false;
        const resetButton = this.querySelector('[data-zoom-action="reset"]');
        if (resetButton) resetButton.textContent = '100%';
      }
    }
  );
}
